import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCcw, ScanSearch, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EmptyState } from "@/components/EmptyState";
import { pct, fmtDate, fmtNum, todayISO, addDaysISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

import SCurveChart from "@/components/project/SCurveChart";
export default function Dashboard() {
  const { projectId, project, isProjectManager } = useProjectCtx();
  const [dashboard, setDashboard] = useState(null);
  const [curve, setCurve] = useState([]);
  const [granularity, setGranularity] = useState("daily");
  const [asOf, setAsOf] = useState(todayISO());
  const [range, setRange] = useState({
    start: project?.start_date || addDaysISO(todayISO(), -90),
    end: project?.target_end_date || todayISO(),
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadDashboard = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/dashboard`, { params: { as_of: asOf || undefined } });   // a cleared date means today, not an empty value the server rejects
    setDashboard(data);
  }, [projectId, asOf]);

  const loadCurve = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/s-curve`, { params: { ...range, granularity } });
    const points = data.points.map((p) => ({ ...p, dateLabel: fmtDate(p.date) }));
    const outlook = (data.outlook || []).map((p) => ({ date: p.date, dateLabel: fmtDate(p.date), outlook_pct: p.projected_pct }));
    setCurve([...points, ...outlook]);
  }, [projectId, range, granularity]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadDashboard(), loadCurve()]).finally(() => setLoading(false));
  }, [loadDashboard, loadCurve]);

  const cards = useMemo(() => {
    if (!dashboard) return [];
    return [
      { label: "Overall", actual: dashboard.overall_actual_pct, planned: dashboard.overall_planned_pct },
      { label: "Engineering", actual: dashboard.categories.ENGINEERING.actual_pct, planned: dashboard.categories.ENGINEERING.planned_pct },
      { label: "Procurement", actual: dashboard.categories.PROCUREMENT.actual_pct, planned: dashboard.categories.PROCUREMENT.planned_pct },
      { label: "Construction", actual: dashboard.categories.CONSTRUCTION.actual_pct, planned: dashboard.categories.CONSTRUCTION.planned_pct },
    ];
  }, [dashboard]);

  const act = async (alertId, status) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/alerts/${alertId}`, { status });
      toast.success(`Alert ${status.toLowerCase()}`);
      await loadDashboard();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rebuild = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/snapshots/rebuild`, {});
      await api.post(`/projects/${projectId}/alerts/scan`, {});
      toast.success("S-curve and alerts refreshed from live progress");
      await Promise.all([loadDashboard(), loadCurve()]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !dashboard) return <p className="text-sm text-muted-foreground" data-testid="dashboard-loading">Loading dashboard…</p>;

  return (
    <div className="space-y-6" data-testid="project-dashboard-page">
      <PageHeader
        title="Project Dashboard"
        description={dashboard ? `${dashboard.project.location || ""} · as of ${dashboard.as_of}` : ""}
        action={
          <div className="flex items-center gap-2">
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="dashboard-as-of-input" className="w-40" />
            {isProjectManager && (
              <Button variant="outline" size="sm" disabled={busy} onClick={rebuild} data-testid="dashboard-rebuild-button">
                <RefreshCcw className="h-3.5 w-3.5" /> Recompute
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-category-cards">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border bg-card p-4" data-testid={`dashboard-card-${c.label.toLowerCase()}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className="mt-2 font-mono text-2xl font-bold text-foreground">{pct(c.actual)}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Planned {pct(c.planned)}</p>
          </div>
        ))}
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="dashboard-counters">
          <Counter label="Open issues" value={dashboard.counters.open_issues} />
          <Counter label="Critical issues" value={dashboard.counters.critical_issues} tone="rose" />
          <Counter label="Open alerts" value={dashboard.counters.open_alerts} tone="amber" />
          <Counter label="Overdue drawings" value={dashboard.counters.overdue_drawings} tone="amber" />
          <Counter label="Overdue materials" value={dashboard.counters.overdue_materials} tone="amber" />
          <Counter label="Overdue activities" value={dashboard.counters.overdue_activities} tone="rose" />
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4" data-testid="s-curve-chart-container">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">S-Curve — Planned vs Actual</h2>
          <div className="flex items-center gap-2">
            <ToggleGroup type="single" value={granularity} onValueChange={(v) => v && setGranularity(v)} data-testid="s-curve-granularity-toggle">
              <ToggleGroupItem value="daily" data-testid="s-curve-granularity-daily">Daily</ToggleGroupItem>
              <ToggleGroupItem value="weekly" data-testid="s-curve-granularity-weekly">Weekly</ToggleGroupItem>
            </ToggleGroup>
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="s-curve-start-input" className="w-36" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="s-curve-end-input" className="w-36" />
          </div>
        </div>
        {curve.length === 0 ? (
          <EmptyState title="No snapshot history yet" description="Click Recompute to build the S-curve from current progress." testId="s-curve-empty-state" />
        ) : (
          <SCurveChart curve={curve} dashboard={typeof dashboard !== 'undefined' ? dashboard : undefined} asOf={typeof asOf !== 'undefined' ? asOf : undefined} />
        )}
      </div>

      <div className="rounded-xl border border-border bg-card" data-testid="top-alerts-list">
        <div className="border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Top Alerts</h2>
        </div>
        {(!dashboard?.top_alerts || dashboard.top_alerts.length === 0) ? (
          <EmptyState title="No open alerts" description="This project has no overdue items right now." testId="top-alerts-empty-state" />
        ) : (
          <ul className="divide-y divide-border">
            {dashboard.top_alerts.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 p-3" data-testid={`top-alert-row-${a.id}`}>
                <div className="flex items-center gap-2">
                  <ShieldAlert className={`h-4 w-4 shrink-0 ${a.severity === "CRITICAL" ? "text-rose-400" : "text-amber-400"}`} />
                  <div>
                    <p className="text-sm text-foreground">{a.title}</p>
                    <p className="text-[11px] text-muted-foreground">{a.alert_type.replaceAll("_", " ")} · due {fmtDate(a.due_date)} · {a.days_overdue}d overdue</p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => act(a.id, "ACKNOWLEDGED")} data-testid={`alert-acknowledge-btn-${a.id}`}>Ack</Button>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(a.id, "RESOLVED")} data-testid={`alert-resolve-btn-${a.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Counter({ label, value, tone }) {
  const toneClass = tone === "rose" ? "text-rose-400" : tone === "amber" ? "text-amber-400" : "text-foreground";
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5" data-testid={`counter-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className={`font-mono text-xl font-bold ${toneClass}`}>{fmtNum(value)}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
